import RepasLogo from "../assets/images/repas_logo.svg";
import LanguageToggle from "@/components/LanguageToggle";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { supabase } from "@/utils/supabase";
import { isLuckyWheelOpen } from "@/constants/challenge";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dimensions,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export const ONBOARDING_SEEN_KEY = "onboarding_seen_v1";

const { width } = Dimensions.get("window");

// Textes traduits au rendu : chaque étape référence ses clés i18n
// (racine `onboarding` dans i18n/locales/fr.json et en.json).
interface Step {
  titleKey: string;
  descriptionKey: string;
  highlightKey?: string;
  creditKey?: string;
  ctaKey: string;
}

const STEPS: Step[] = [
  {
    titleKey: "onboarding.step1.title",
    descriptionKey: "onboarding.step1.description",
    creditKey: "onboarding.step1.credit",
    ctaKey: "onboarding.next",
  },
  {
    titleKey: "onboarding.step2.title",
    descriptionKey: "onboarding.step2.description",
    highlightKey: "onboarding.step2.highlight",
    ctaKey: "onboarding.next",
  },
  {
    titleKey: "onboarding.step3.title",
    descriptionKey: "onboarding.step3.description",
    ctaKey: "onboarding.start",
  },
];

export default function OnboardingScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  // `loggedIn=1` est passé par login.tsx / index.tsx quand l'utilisateur est
  // DÉJÀ authentifié (cas admin qui revoit l'onboarding). Source de vérité
  // déterministe, contrairement à getSession() qui peut renvoyer null par
  // timing d'hydratation sur le web (→ la roue s'ouvrait à tort).
  const params = useLocalSearchParams<{ loggedIn?: string }>();
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const handleNext = useCallback(async () => {
    if (!isLast) {
      setStepIndex((i) => i + 1);
      return;
    }
    try {
      await AsyncStorage.setItem(ONBOARDING_SEEN_KEY, "true");
    } catch {
      // Storage failure is non-blocking — the user can re-onboard if needed.
    }
    // Un utilisateur déjà connecté (cas admin, qui revoit l'onboarding à chaque
    // connexion) entre directement dans l'app au lieu du parcours d'inscription
    // (roue → création de compte) réservé aux nouveaux visiteurs.
    if (params.loggedIn === "1") {
      router.replace("/(tabs)");
      return;
    }
    // Filet de sécurité (entrée directe sur l'onboarding sans le param).
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        router.replace("/(tabs)");
        return;
      }
    } catch {
      // En cas d'échec de lecture de session, on retombe sur le parcours normal.
    }
    router.replace(isLuckyWheelOpen() ? "/onboarding-wheel" : "/create-account");
  }, [isLast, router, params.loggedIn]);

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <LinearGradient
        colors={["#FCC189", "#CD7728", "#CD7728"]}
        locations={[0, 0.41, 1]}
        style={styles.background}
      >
        <SafeAreaView style={styles.content}>
          <LanguageToggle />
          <RepasLogo width={width * 0.6} height={80} style={styles.logo} />

          <View style={styles.textContainer}>
            <Text style={styles.dateText}>{t("onboarding.dateRange")}</Text>

            <View
              style={styles.paginationContainer}
              accessibilityRole="progressbar"
              accessibilityLabel={t("onboarding.stepProgress", {
                current: stepIndex + 1,
                total: STEPS.length,
              })}
            >
              {STEPS.map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.paginationDot,
                    i === stepIndex && styles.paginationDotActive,
                  ]}
                />
              ))}
            </View>

            <Text style={styles.title} accessibilityRole="header">
              {t(step.titleKey)}
            </Text>
            <Text style={styles.description}>{t(step.descriptionKey)}</Text>
            {step.highlightKey ? (
              <Text style={styles.highlight}>{t(step.highlightKey)}</Text>
            ) : null}
            {step.creditKey ? (
              <Text style={styles.credit}>{t(step.creditKey)}</Text>
            ) : null}
          </View>

          <TouchableOpacity
            style={styles.buttonContainer}
            onPress={handleNext}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel={t(step.ctaKey)}
          >
            <LinearGradient
              colors={["#1C453F", "#78A584"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.button}
            >
              <Text style={styles.buttonText}>{t(step.ctaKey)}</Text>
            </LinearGradient>
          </TouchableOpacity>
        </SafeAreaView>
      </LinearGradient>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  content: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 24,
    justifyContent: "space-between",
    paddingTop: 40,
    paddingBottom: 60,
  },
  logo: {
    width: width * 0.6,
    height: 80,
    alignSelf: "center",
  },
  textContainer: {
    width: "100%",
  },
  dateText: {
    fontFamily: "Inter_700Bold",
    fontSize: 24,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 40,
  },
  paginationContainer: {
    flexDirection: "row",
    marginBottom: 20,
  },
  paginationDot: {
    width: 30,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    marginRight: 8,
  },
  paginationDotActive: {
    backgroundColor: "#733801",
    width: 30,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#FFFFFF",
    marginBottom: 16,
    lineHeight: 34,
  },
  description: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#FFFFFF",
    lineHeight: 24,
    opacity: 0.9,
  },
  highlight: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#FFFFFF",
    lineHeight: 24,
    marginTop: 12,
  },
  credit: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#FFFFFF",
    lineHeight: 18,
    opacity: 0.8,
    marginTop: 16,
  },
  buttonContainer: {
    width: "100%",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    marginBottom: 30,
  },
  button: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    width: "100%",
  },
  buttonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFFFFF",
  },
});
